/**
 * TaskTypeMachinesEditor.tsx
 * Phase 5.6 — "Machines & Time" editor for the test/task-type side.
 *
 * Opens from the Test Catalogue detail pane's "Machines & Time" button and the
 * Operational Steps dialog (PR B — TaskTypesPanelDialog is retired). An admin
 * picks the machine(s) a test runs on and sets its minutes — writing the same
 * MachineTaskCapability(+MachineMinutes) rows the machine-side editor manages.
 * One source of truth, multiple entry points. (D5, §3.4)
 *
 * Set-replace semantics:
 *   - Upsert every desired {MachineKey, MachineMinutes}
 *   - Soft-delete capabilities removed from the list
 *   - Note: the hard-delete guard (live-booking refusal) is item 5.7's job;
 *     soft-delete on removal is safe here and leaves 5.7 implementable.
 *
 * All API calls use apiClient-backed service functions (scheduleTaxonomy.ts).
 * No hex literals — colors via theme.schedule.* tokens.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import { Close, HardwareOutlined, Star, StarBorder } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import {
  fetchTaskTypeCapabilities,
  setTaskTypeCapabilities,
  type TaskTypeCapabilityRow,
} from '../../../services/scheduleTaxonomy';
import { fetchMachines, type MachineRow } from '../../../services/scheduleMachines';
// Run Mode Phase A: per-mode minutes semantics helper text.
import { minutesSemanticsHint, type TaskRunMode } from '../../../utils/runMode';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  taskTypeKey: string;
  taskTypeName: string;
  /**
   * Run Mode Phase A: the task type's run mode — switches the minutes helper
   * text ('attended' packs into 8h working days; 'continuous' is total 24/7
   * elapsed time with a live calendar-day preview). Defaults to 'attended'.
   */
  runMode?: TaskRunMode;
  onClose: () => void;
  /** Called when capabilities are saved so the parent can refresh its list. */
  onSaved?: () => void;
}

/**
 * Internal state for a machine row in the editor:
 *   selected machine + raw minutes string for the TextField.
 */
interface CapRow {
  machine: MachineRow;
  minutesRaw: string; // empty string = null
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseMinutes(raw: string): number | null {
  if (!raw.trim()) return null;
  const n = parseInt(raw, 10);
  return isNaN(n) || n < 0 ? null : n;
}

function isValidMinutes(raw: string): boolean {
  if (!raw.trim()) return true;
  const n = parseInt(raw, 10);
  return !isNaN(n) && n >= 0 && String(n) === raw.trim();
}

// ── Component ─────────────────────────────────────────────────────────────────

export const TaskTypeMachinesEditor: React.FC<Props> = ({
  open,
  taskTypeKey,
  taskTypeName,
  runMode = 'attended',
  onClose,
  onSaved,
}) => {
  const theme = useTheme() as Theme;

  const [allMachines, setAllMachines] = useState<MachineRow[]>([]);
  const [capRows, setCapRows] = useState<CapRow[]>([]);
  // REQ-06: MachineKey of the admin-chosen default machine for this TaskType
  // (auto-pick honors it). null = no default (auto-pick falls back to Code order).
  const [defaultKey, setDefaultKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // ── Load machines + current capabilities on open ──────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const [machines, caps] = await Promise.all([
        fetchMachines(),
        fetchTaskTypeCapabilities(taskTypeKey),
      ]);
      setAllMachines(machines);

      // Build CapRow list from the current capabilities
      const capRowList: CapRow[] = [];
      let loadedDefault: string | null = null;
      for (const cap of caps as TaskTypeCapabilityRow[]) {
        const machine = machines.find((m) => m.MachineKey === cap.MachineKey);
        if (machine) {
          capRowList.push({
            machine,
            minutesRaw: cap.MachineMinutes != null ? String(cap.MachineMinutes) : '',
          });
          if (cap.IsDefault) loadedDefault = cap.MachineKey;
        }
      }
      setCapRows(capRowList);
      setDefaultKey(loadedDefault);
    } catch {
      setErrorMsg('Failed to load machines or capabilities');
    } finally {
      setLoading(false);
    }
  }, [taskTypeKey]);

  useEffect(() => {
    if (open && taskTypeKey) {
      loadData();
    }
  }, [open, taskTypeKey, loadData]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  /** Multi-select change: keep minutes for retained machines, discard removed ones. */
  const handleMachinesChange = (
    _e: React.SyntheticEvent,
    selectedMachines: MachineRow[],
  ) => {
    setCapRows((prev) => {
      const prevMap = new Map(prev.map((r) => [r.machine.MachineKey, r.minutesRaw]));
      return selectedMachines.map((machine) => ({
        machine,
        minutesRaw: prevMap.get(machine.MachineKey) ?? '',
      }));
    });
    // REQ-06: if the default machine was deselected, clear the default.
    setDefaultKey((prev) =>
      prev && selectedMachines.some((m) => m.MachineKey === prev) ? prev : null,
    );
  };

  /** REQ-06: pick (or toggle off) the default machine for auto-pick. */
  const handleDefaultChange = (machineKey: string) => {
    setDefaultKey((prev) => (prev === machineKey ? null : machineKey));
  };

  const handleMinutesChange = (machineKey: string, value: string) => {
    if (value !== '' && !/^\d+$/.test(value)) return;
    setCapRows((prev) =>
      prev.map((r) =>
        r.machine.MachineKey === machineKey ? { ...r, minutesRaw: value } : r,
      ),
    );
  };

  const handleSave = async () => {
    // Validate
    for (const row of capRows) {
      if (!isValidMinutes(row.minutesRaw)) {
        setErrorMsg(
          `Machine Minutes for "${row.machine.Name}" must be a whole number ≥ 0 (or leave blank).`,
        );
        return;
      }
    }

    setSaving(true);
    setErrorMsg(null);
    try {
      const payload = capRows.map((row) => ({
        MachineKey: row.machine.MachineKey,
        MachineMinutes: parseMinutes(row.minutesRaw),
        IsDefault: row.machine.MachineKey === defaultKey,
      }));
      await setTaskTypeCapabilities(taskTypeKey, payload);
      onSaved?.();
      onClose();
    } catch (err) {
      // Phase 5.7: 409 LIVE_BOOKING_GUARD — show API body message directly.
      const msg =
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
        (err instanceof Error ? err.message : 'Failed to save');
      setErrorMsg(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const selectedMachines = capRows.map((r) => r.machine);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HardwareOutlined sx={{ color: theme.schedule.machineTime }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Machines &amp; Time
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {taskTypeName}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg(null)}>
            {errorMsg}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Helper text — per-mode minutes semantics (Run Mode Phase A) */}
            <Typography variant="body2" color="text.secondary">
              Select the machine(s) this test runs on and set the machine time in minutes.
              {runMode === 'continuous'
                ? ' This test is CONTINUOUS: minutes are total elapsed time — it runs 24/7 once started (machine time counts calendar days, weekends included).'
                : ' Minutes pack into 8-hour working days.'}
              {' '}Unmapped tests cannot be automatically scheduled. Star one machine as the
              default — auto-scheduling will prefer it when a test can run on several machines.
            </Typography>

            {/* Machine multi-select */}
            <Autocomplete
              multiple
              size="small"
              options={allMachines.filter((m) => m.IsActive)}
              getOptionLabel={(opt) => `${opt.Code} — ${opt.Name}`}
              value={selectedMachines}
              onChange={handleMachinesChange}
              isOptionEqualToValue={(opt, val) => opt.MachineKey === val.MachineKey}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Machines (capabilities)"
                  placeholder={capRows.length === 0 ? 'Pick a machine…' : undefined}
                />
              )}
              groupBy={(opt) => opt.LabSection ?? 'Other'}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.MachineKey}
                    label={option.Code}
                    size="small"
                    {...getTagProps({ index })}
                    sx={{ bgcolor: theme.schedule.machineTime, color: 'common.white', fontWeight: 600 }}
                  />
                ))
              }
            />

            {/* Per-machine minutes inputs */}
            {capRows.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Default machine (star) &amp; machine minutes per test (leave blank if unknown)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {capRows.map((row) => {
                    const invalid = !isValidMinutes(row.minutesRaw);
                    const isDefault = row.machine.MachineKey === defaultKey;
                    return (
                      <Box
                        key={row.machine.MachineKey}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                      >
                        <Tooltip
                          title={isDefault ? 'Default machine for auto-pick' : 'Set as default machine'}
                        >
                          <Radio
                            size="small"
                            checked={isDefault}
                            onClick={() => handleDefaultChange(row.machine.MachineKey)}
                            icon={<StarBorder fontSize="small" />}
                            checkedIcon={<Star fontSize="small" />}
                            sx={{ p: 0.5, color: theme.schedule.machineTime }}
                            inputProps={{ 'aria-label': `Set ${row.machine.Code} as default machine` }}
                          />
                        </Tooltip>
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={`${row.machine.Code} — ${row.machine.Name}`}
                        >
                          <strong>{row.machine.Code}</strong> — {row.machine.Name}
                          {isDefault && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ ml: 0.5, color: theme.schedule.machineTime, fontWeight: 600 }}
                            >
                              (default)
                            </Typography>
                          )}
                        </Typography>
                        <TextField
                          size="small"
                          label="Minutes"
                          value={row.minutesRaw}
                          onChange={(e) =>
                            handleMinutesChange(row.machine.MachineKey, e.target.value)
                          }
                          error={invalid && row.minutesRaw !== ''}
                          helperText={
                            invalid && row.minutesRaw !== ''
                              ? 'Must be a whole number ≥ 0'
                              // Run Mode Phase A: per-mode semantics + live
                              // calendar-day preview for continuous types.
                              : minutesSemanticsHint(runMode, parseMinutes(row.minutesRaw))
                          }
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 5 }}
                          sx={{ width: 190 }}
                          placeholder="e.g. 30"
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {capRows.length === 0 && !loading && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No machines mapped. Select at least one machine above to make this test schedulable.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
          sx={{ fontWeight: 600 }}
        >
          {saving ? 'Saving…' : 'Save Mappings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
